import type { PermissionApi } from '@data-delivery/permission-ui-api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { UserFormValues } from '../schemas/user-form.schema';
import { createUserFormSchema } from '../schemas/user-form.schema';

const DEFAULT_IP_WHITELIST = '0.0.0.0/0';

type UserEntityLike =
  | PermissionApi.UserEntity
  | {
      id: string;
      loginId: string;
      distributionList?: string[];
      ipWhitelist?: string[];
      landingFolder?: string;
      sfAccountId?: string;
      contentAccessRole?: string;
      orgName?: string;
      rootFolder?: string;
      serviceAccountOwner?: string;
      lastModified?: number;
      assignments?: string[];
      firstName?: string;
      lastName?: string;
      isSystemUser?: boolean;
      isLoginAdmin?: boolean;
      isEnabled?: boolean;
    };

interface UseUserFormOptions {
  mode: 'create' | 'edit';
  user?: UserEntityLike;
  restrictedNames: Set<string>;
  restrictedFolders: Set<string>;
  requireAssignments: boolean;
  currentUserRoles?: Array<{ role: string }>;
}

function transformUserToFormValues(user?: UserEntityLike): UserFormValues | undefined {
  if (!user) {
    return undefined;
  }

  return {
    loginId: user.loginId,
    distributionList: (user.distributionList || []).join(', '),
    ipWhitelist:
      user.ipWhitelist && user.ipWhitelist.length > 0
        ? user.ipWhitelist.join(', ')
        : DEFAULT_IP_WHITELIST,
    landingFolder: user.landingFolder || '',
    sfAccountId: user.sfAccountId || '',
    contentAccessRole: user.contentAccessRole || '',
    orgName: user.orgName || '',
    rootFolder: user.rootFolder || '',
    serviceAccountOwner: user.serviceAccountOwner || '',
    assignments: user.assignments || [],
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    isSystemUser: Boolean(user.isSystemUser),
    isLoginAdmin: Boolean(user.isLoginAdmin),
    isEnabled: Boolean(user.isEnabled),
  };
}

const DEFAULT_FORM_VALUES: UserFormValues = {
  loginId: '',
  distributionList: '',
  ipWhitelist: DEFAULT_IP_WHITELIST,
  landingFolder: '',
  sfAccountId: '',
  contentAccessRole: '',
  orgName: '',
  rootFolder: '',
  serviceAccountOwner: '',
  assignments: [],
  firstName: '',
  lastName: '',
  isSystemUser: false,
  isLoginAdmin: false,
  isEnabled: false,
};

export function useUserForm({
  mode,
  user,
  restrictedNames,
  restrictedFolders,
  requireAssignments,
  currentUserRoles,
}: UseUserFormOptions) {
  const schema = useMemo(
    () =>
      createUserFormSchema({
        restrictedNames,
        restrictedFolders,
        requireAssignments,
        currentUserRoles,
      }),
    [restrictedNames, restrictedFolders, requireAssignments, currentUserRoles],
  );

  const formValues = useMemo(
    () => (user ? transformUserToFormValues(user) : DEFAULT_FORM_VALUES),
    [user],
  );

  return useForm<UserFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_FORM_VALUES,
    values: mode === 'edit' ? formValues : undefined,
  });
}
